import { useCallback } from 'react';
import { billingService } from '@/services';
import type { BillingPreviewParams } from '@/services/billing/billing-service';
import type { BillingStatementType, StatementExportFormat } from '@/types';
import { useBackgroundExport } from './use-background-export';

/** Which preview document an export is of. */
export interface PreviewExportTarget {
  counterparty: number;
  statement_type: BillingStatementType;
}

const EXTENSION: Record<StatementExportFormat, string> = {
  csv: 'csv',
  xlsx: 'xlsx',
  pdf: 'pdf',
};

/**
 * Download an MTD preview statement as a file (LR-508 / T5).
 *
 * The preview has no id, so it exports by params (POST /billing/preview/export/).
 * Reuses the shared `useBackgroundExport` core (LR-728) - identical export ->
 * save behaviour as the generated-statement download, just a different export
 * endpoint. A month with nothing accrued ends the export as failed, with the
 * server's reason in the toast. `exportTarget` says which counterparty and
 * document kind the running export is of.
 */
export const useDownloadPreview = () => {
  const { start, isExporting, job, target, exportError } =
    useBackgroundExport<PreviewExportTarget>();

  const download = useCallback(
    (
      params: BillingPreviewParams & { statement_type?: BillingStatementType },
      format: StatementExportFormat = 'csv'
    ) => {
      // Carry every filter the preview narrows on (account, currency,
      // agreement): since LR-553 the default download is the whole document
      // (counterparty only), but a narrowed call still names its file by what
      // it narrowed on. Any dropped component collides two different files onto
      // one name on this path (used only when the server omits
      // Content-Disposition).
      // `account` is operator-entered free text: strip anything that would make
      // the download attribute browser-dependent (separators, newlines) (LR-552).
      const account = params.account?.replace(/[^\w.-]+/g, '-');
      // The cash pool preview (LR-624) is a SECOND document for the same
      // counterparty and period - without its own base name the two previews
      // would collide onto one filename on this fallback path.
      const kind =
        params.statement_type === 'cash_pool_interest'
          ? 'cash_pool_preview'
          : 'billing_preview';
      const base = [kind, params.currency, params.agreement, account]
        .filter(Boolean)
        .join('_');
      return start(
        () => billingService.startPreviewExport({ ...params, format }),
        `${base}.${EXTENSION[format]}`,
        {
          counterparty: params.counterparty,
          statement_type: params.statement_type ?? 'trade_billing',
        }
      );
    },
    [start]
  );

  return {
    download,
    isDownloading: isExporting,
    exportJob: job,
    exportTarget: target,
    downloadError: exportError,
  };
};
